/**
 * commonUtils — Shared utility functions used across multiple components.
 */
import MuiAlert from "./muiAlerts"

/** Escapes special regex characters in a string so it can be used in a RegExp constructor safely. */
function escapeRegex(string) {
    return string.replace(/[/\-\\^$*+?.()|[\]{}]/g, '\\$&');
}

/**
 * Returns a react-select styles object that matches the app's light/dark theme.
 * Colours are hand-matched to the MUI Slate dark palette and default light palette.
 * @param {boolean} isDark - Pass `true` when the app is in dark mode
 */
const dropDownStyle = (isDark = false) => ({
    control: (styles) => ({
        ...styles,
        backgroundColor: isDark ? '#1e293b' : '#fff',
        borderColor: isDark ? 'rgba(255,255,255,0.6)' : '#000',
        borderWidth: '2px',
        boxShadow: 'none',
        '&:hover': { borderColor: isDark ? '#fff' : '#000' },
    }),
    menu: (styles) => ({
        ...styles,
        minWidth: 'max-content',
        backgroundColor: isDark ? '#1e293b' : '#fff',
        zIndex: 9999,
    }),
    menuList: (styles) => ({
        ...styles,
        backgroundColor: isDark ? '#1e293b' : '#fff',
        padding: 0,
    }),
    option: (styles, { isFocused, isSelected }) => ({
        ...styles,
        backgroundColor: isSelected
            ? (isDark ? '#3b82f6' : '#1976d2')
            : isFocused
                ? (isDark ? '#334155' : '#e8f4ff')
                : (isDark ? '#1e293b' : '#fff'),
        color: isDark ? '#fff' : '#000',
        cursor: 'pointer',
    }),
    singleValue: (styles) => ({
        ...styles,
        color: isDark ? '#fff' : '#000',
    }),
    placeholder: (styles) => ({
        ...styles,
        color: isDark ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)',
    }),
    input: (styles) => ({
        ...styles,
        color: isDark ? '#fff' : '#000',
    }),
    indicatorSeparator: (styles) => ({
        ...styles,
        backgroundColor: isDark ? 'rgba(255,255,255,0.25)' : 'rgba(0,0,0,0.2)',
    }),
    dropdownIndicator: (styles) => ({
        ...styles,
        color: isDark ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.5)',
        '&:hover': { color: isDark ? '#fff' : '#000' },
    }),
})

/** Formats a numeric patient number as a zero-padded string: 12 → "PAT0012" */
function formatPatientNumber(number) {
    return `PAT${String(number).padStart(4, "0")}`;
}

/** Formats a numeric amount using Indian locale notation with up to full decimal precision. */
function formatAmount(amount) {
    let formattedNumber = parseFloat(amount).toLocaleString('en-IN', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 20
    });

    return formattedNumber;
}

/** Opens a Google Maps link for the given coordinates. Shows an alert if coordinates are missing. */
function viewLocation({ latitude, longitude }){
    if (!latitude || !longitude) {
        MuiAlert.fire("Oops!", "Map-coordinates not available", "info")
        return
    }

    window.open(`https://maps.google.com/?q=${latitude},${longitude}`)
}

export { escapeRegex, dropDownStyle, formatPatientNumber, formatAmount, viewLocation }